import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent } from "../components/ui/card";
import { ChevronDown, ChevronRight, FileSpreadsheet, FileText } from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { useSortableTable } from '../hooks/useSortableTable';
import { SortableTh } from './ui/SortableTh';

const ReporteDinamico = ({ data }) => {
  const [groupBy, setGroupBy] = useState('Agente');
  const [expandedRows, setExpandedRows] = useState({});

  const normalizeBrand = (brand) => {
    if (!brand) return 'Desconocida';
    let cleaned = brand.trim().replace(/\s+/g, ' ').toLowerCase();

    if (['vw', 'volskwagen', 'vokswagen', 'volkswen'].includes(cleaned)) cleaned = 'volkswagen';
    if (cleaned === 'chevy') cleaned = 'chevrolet';
    if (['mb', 'mercedes', 'mercedes benz'].includes(cleaned)) cleaned = 'mercedes-benz';
    if (cleaned === 'bm') cleaned = 'bmw';
    if (cleaned === 'peugot') cleaned = 'peugeot';

    return cleaned.split(/[\s-]+/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const toggleRow = (name) => {
    setExpandedRows(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const groupedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    const map = {};

    data.forEach(row => {
      let key;
      if (groupBy === 'Revendedor') {
        if (!row['Oferta ID']) {
          return; // Ignorar publicaciones sin oferta cuando se agrupa por revendedor
        }
        const nombre = row['Revendedor Nombre'] || 'Sin nombre';
        const empresa = row['Revendedor Empresa'] ? ` (${row['Revendedor Empresa']})` : '';
        key = `${nombre}${empresa}`;
      } else if (groupBy === 'Marca') {
        key = normalizeBrand(row['Marca']);
      } else {
        key = String(row[groupBy] ?? 'Desconocido');
      }
      
      if (!map[key]) {
        map[key] = {
          name: key,
          publicaciones: 0,
          adjudicadas: 0,
          noAdjudicadas: 0,
          ofertas: 0,
          montoAdjudicado: 0,
          montoMinimo: null,
          montoMaximo: null,
          modelos: {} // Used when grouping by Marca
        };
      }

      const item = map[key];

      // Process vehicle-level metrics (only once per vehicle)
      if (row["Conteo Publicacion"] === 1) {
        item.publicaciones += 1;
        
        const estado = row["Estado Publicacion"];
        if (estado === "Adjudicada") {
          item.adjudicadas += 1;
          const precioAdj = row["Precio Adjudicado"] || 0;
          item.montoAdjudicado += precioAdj;
          if (item.montoMinimo === null || precioAdj < item.montoMinimo) item.montoMinimo = precioAdj;
          if (item.montoMaximo === null || precioAdj > item.montoMaximo) item.montoMaximo = precioAdj;
        } else if (estado === "No adjudicada") {
          item.noAdjudicadas += 1;
        }

        // Track model sub-aggregations if grouping by Marca
        if (groupBy === 'Marca') {
          const modelo = row['Modelo'] || 'Desconocido';
          if (!item.modelos[modelo]) {
            item.modelos[modelo] = {
              publicaciones: 0,
              adjudicadas: 0,
              noAdjudicadas: 0,
              ofertas: 0,
              montoAdjudicado: 0,
              montoMinimo: null,
              montoMaximo: null
            };
          }
          item.modelos[modelo].publicaciones += 1;
          if (estado === "Adjudicada") {
            item.modelos[modelo].adjudicadas += 1;
            const precioAdj = row["Precio Adjudicado"] || 0;
            item.modelos[modelo].montoAdjudicado += precioAdj;
            if (item.modelos[modelo].montoMinimo === null || precioAdj < item.modelos[modelo].montoMinimo) item.modelos[modelo].montoMinimo = precioAdj;
            if (item.modelos[modelo].montoMaximo === null || precioAdj > item.modelos[modelo].montoMaximo) item.modelos[modelo].montoMaximo = precioAdj;
          } else if (estado === "No adjudicada") {
            item.modelos[modelo].noAdjudicadas += 1;
          }
        }
      }
      
      // Process offer-level metrics
      if (row["Oferta ID"]) {
        item.ofertas += 1;
        if (groupBy === 'Marca' && row["Conteo Publicacion"] === 1) {
           // We only have the first row of each vehicle in this loop context for model tracking, 
           // but actually offers are on multiple rows. We need to track offers for models properly.
           // To avoid double loop logic, we just do it here.
        }
      }
    });

    // Fix: Ofertas for modelos needs to be counted for every row if it's an offer
    if (groupBy === 'Marca') {
       data.forEach(row => {
          if (row["Oferta ID"]) {
             const key = normalizeBrand(row['Marca']);
             const modelo = row['Modelo'] || 'Desconocido';
             if (map[key] && map[key].modelos[modelo]) {
                // Ensure we only count offers if they exist
                map[key].modelos[modelo].ofertas += 1;
             }
          }
       });
       
       // Because we double-counted above in the first loop's first row, we need to reset and accurately count them.
       // Actually the first loop didn't count offers for models! We only added the property.
    }

    // We do not sort here anymore, we'll sort dynamically below
    return Object.values(map);
  }, [data, groupBy]);

  const { items: sortedData, requestSort, sortConfig } = useSortableTable(groupedData, { key: 'publicaciones', direction: 'desc' });

  if (!data || data.length === 0) {
    return <div className="text-center p-8 text-muted-foreground">No hay datos para el período seleccionado.</div>;
  }

  // Calculate Totals
  const totalPubs = groupedData.reduce((acc, curr) => acc + curr.publicaciones, 0);
  const totalAdj = groupedData.reduce((acc, curr) => acc + curr.adjudicadas, 0);
  const totalNoAdj = groupedData.reduce((acc, curr) => acc + curr.noAdjudicadas, 0);
  const totalOfertas = groupedData.reduce((acc, curr) => acc + curr.ofertas, 0);
  const totalMonto = groupedData.reduce((acc, curr) => acc + curr.montoAdjudicado, 0);
  const totalMinMonto = groupedData.reduce((acc, curr) => {
    if (curr.montoMinimo === null) return acc;
    if (acc === null || curr.montoMinimo < acc) return curr.montoMinimo;
    return acc;
  }, null);
  const totalMaxMonto = groupedData.reduce((acc, curr) => {
    if (curr.montoMaximo === null) return acc;
    if (acc === null || curr.montoMaximo > acc) return curr.montoMaximo;
    return acc;
  }, null);

  const handleExportCSV = () => {
    let csv = `${groupBy},Total Pubs.,Adjudicadas,No Adj.,Ofertas,Monto Adj. (USD),Mínimo Adj.,Máximo Adj.\n`;
    
    sortedData.forEach(row => {
      const name = String(row.name === 'true' ? 'Sí' : row.name === 'false' ? 'No' : row.name).replace(/,/g, '');
      csv += `${name},${row.publicaciones},${row.adjudicadas},${row.noAdjudicadas},${row.ofertas},${row.montoAdjudicado},${row.montoMinimo ?? '-'},${row.montoMaximo ?? '-'}\n`;
      
      if (groupBy === 'Marca' && expandedRows[row.name]) {
        Object.entries(row.modelos)
          .sort((a, b) => b[1].publicaciones - a[1].publicaciones)
          .forEach(([modelo, metrics]) => {
             const mName = String(modelo).replace(/,/g, '');
             csv += ` - ${mName},${metrics.publicaciones},${metrics.adjudicadas},${metrics.noAdjudicadas},${metrics.ofertas},${metrics.montoAdjudicado},${metrics.montoMinimo ?? '-'},${metrics.montoMaximo ?? '-'}\n`;
          });
      }
    });

    csv += `TOTALES GLOBALES,${totalPubs},${totalAdj},${totalNoAdj},${totalOfertas},${totalMonto},${totalMinMonto ?? '-'},${totalMaxMonto ?? '-'}\n`;

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `reporte_dinamico_${new Date().getTime()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = async () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    
    doc.setFontSize(16);
    doc.text(`Reporte Dinámico: Agrupado por ${groupBy}`, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generado: ${new Date().toLocaleDateString()}`, 14, 28);
    
    let yPos = 35;
    
    const chartEl = document.getElementById('reporte-chart');
    if (chartEl) {
      try {
        const canvas = await html2canvas(chartEl, { 
          scale: 2, 
          backgroundColor: '#ffffff' 
        });
        const imgData = canvas.toDataURL('image/png');
        
        const imgWidth = pageWidth - 28;
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        
        doc.addImage(imgData, 'PNG', 14, yPos, imgWidth, imgHeight);
        yPos += imgHeight + 10;
      } catch (e) {
        console.error("Error capturing chart for PDF:", e);
      }
    }
    
    const tableData = [];
    sortedData.forEach(row => {
      const name = row.name === 'true' ? 'Sí' : row.name === 'false' ? 'No' : row.name;
      tableData.push([
        name,
        row.publicaciones,
        row.adjudicadas,
        row.noAdjudicadas,
        row.ofertas,
        `$${row.montoAdjudicado.toLocaleString()}`,
        row.montoMinimo !== null ? `$${row.montoMinimo.toLocaleString()}` : '-',
        row.montoMaximo !== null ? `$${row.montoMaximo.toLocaleString()}` : '-'
      ]);
      
      if (groupBy === 'Marca' && expandedRows[row.name]) {
        Object.entries(row.modelos)
          .sort((a, b) => b[1].publicaciones - a[1].publicaciones)
          .forEach(([modelo, metrics]) => {
             tableData.push([
               `  - ${modelo}`,
               metrics.publicaciones,
               metrics.adjudicadas,
               metrics.noAdjudicadas,
               metrics.ofertas,
               `$${metrics.montoAdjudicado.toLocaleString()}`,
               metrics.montoMinimo !== null ? `$${metrics.montoMinimo.toLocaleString()}` : '-',
               metrics.montoMaximo !== null ? `$${metrics.montoMaximo.toLocaleString()}` : '-'
             ]);
          });
      }
    });
    
    tableData.push([
      'TOTALES GLOBALES',
      totalPubs,
      totalAdj,
      totalNoAdj,
      totalOfertas,
      `$${totalMonto.toLocaleString()}`,
      totalMinMonto !== null ? `$${totalMinMonto.toLocaleString()}` : '-',
      totalMaxMonto !== null ? `$${totalMaxMonto.toLocaleString()}` : '-'
    ]);

    doc.autoTable({
      startY: yPos,
      head: [[groupBy, 'Pubs.', 'Adj.', 'No Adj.', 'Ofertas', 'Monto Adj.', 'Mín. Adj.', 'Máx. Adj.']],
      body: tableData,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246] }, 
      willDrawCell: function(data) {
        if (data.row.index === tableData.length - 1) {
          doc.setFont(undefined, 'bold');
          doc.setFillColor(240, 240, 240);
        }
      }
    });

    doc.save(`reporte_dinamico_${new Date().getTime()}.pdf`);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-muted/30 p-4 rounded-lg border">
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium">Agrupar datos por:</span>
          <Select value={groupBy} onValueChange={(val) => { setGroupBy(val); setExpandedRows({}); }}>
            <SelectTrigger className="w-[200px] bg-background">
              <SelectValue placeholder="Agrupar por..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Agente">Vendedor / Agente</SelectItem>
              <SelectItem value="Marca">Marca de Vehículo</SelectItem>
              <SelectItem value="Revendedor">Revendedor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center space-x-2">
           <button onClick={handleExportCSV} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-background border rounded-md hover:bg-muted transition-colors">
             <FileSpreadsheet size={16} className="text-green-600" />
             Exportar CSV
           </button>
           <button onClick={handleExportPDF} className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium bg-background border rounded-md hover:bg-muted transition-colors">
             <FileText size={16} className="text-red-500" />
             Exportar PDF
           </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardContent className="pt-6">
            <div id="reporte-chart" className="h-[400px] w-full p-2 bg-background">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sortedData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                  <XAxis 
                    dataKey="name" 
                    angle={-45}
                    textAnchor="end"
                    interval={0}
                    height={80}
                    tick={{fontSize: 12}}
                    tickFormatter={(val) => val === 'true' ? 'Sí' : val === 'false' ? 'No' : val}
                  />
                  <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                  <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                  <Tooltip 
                    cursor={{fill: 'rgba(0,0,0,0.05)'}}
                    contentStyle={{
                      backgroundColor: 'hsl(var(--background))', 
                      color: 'hsl(var(--foreground))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px', 
                      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                    }}
                    labelFormatter={(label) => label === 'true' ? 'Sí' : label === 'false' ? 'No' : label}
                  />
                  <Legend wrapperStyle={{paddingTop: '20px'}} />
                  <Bar yAxisId="left" dataKey="publicaciones" name="Total Publicaciones" fill="#8884d8" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="left" dataKey="adjudicadas" name="Adjudicadas" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar yAxisId="right" dataKey="ofertas" name="Total Ofertas" fill="#82ca9d" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6 overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase bg-muted/50 text-muted-foreground border-b">
                <tr>
                  <SortableTh label={groupBy} sortKey="name" align="left" className="px-6" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="Total Pubs." sortKey="publicaciones" align="right" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="Adjudicadas" sortKey="adjudicadas" align="right" className="text-green-600" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="No Adj." sortKey="noAdjudicadas" align="right" className="text-red-600" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="Ofertas" sortKey="ofertas" align="right" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="Monto Adj. (USD)" sortKey="montoAdjudicado" align="right" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="Mínimo (USD)" sortKey="montoMinimo" align="right" requestSort={requestSort} sortConfig={sortConfig} />
                  <SortableTh label="Máximo (USD)" sortKey="montoMaximo" align="right" requestSort={requestSort} sortConfig={sortConfig} />
                </tr>
              </thead>
              <tbody>
                {sortedData.map((row, i) => (
                  <React.Fragment key={i}>
                    <tr className="border-b last:border-0 hover:bg-muted/20 transition-colors">
                      <td className="px-6 py-4 font-medium flex items-center gap-2">
                        {groupBy === 'Marca' && Object.keys(row.modelos || {}).length > 0 && (
                          <button onClick={() => toggleRow(row.name)} className="p-1 hover:bg-muted rounded-md transition-colors">
                            {expandedRows[row.name] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </button>
                        )}
                        {row.name === 'true' ? 'Sí' : row.name === 'false' ? 'No' : row.name}
                      </td>
                      <td className="px-4 py-4 text-right">{row.publicaciones}</td>
                      <td className="px-4 py-4 text-right text-green-600 font-medium">{row.adjudicadas}</td>
                      <td className="px-4 py-4 text-right text-red-600">{row.noAdjudicadas}</td>
                      <td className="px-4 py-4 text-right">{row.ofertas}</td>
                      <td className="px-4 py-4 text-right font-medium">${row.montoAdjudicado.toLocaleString()}</td>
                      <td className="px-4 py-4 text-right">{row.montoMinimo !== null ? `$${row.montoMinimo.toLocaleString()}` : '-'}</td>
                      <td className="px-4 py-4 text-right">{row.montoMaximo !== null ? `$${row.montoMaximo.toLocaleString()}` : '-'}</td>
                    </tr>
                    
                    {/* Nested rows for models when grouping by Marca */}
                    {groupBy === 'Marca' && expandedRows[row.name] && Object.entries(row.modelos)
                      .sort((a, b) => b[1].publicaciones - a[1].publicaciones)
                      .map(([modelo, metrics], j) => (
                        <tr key={`model-${i}-${j}`} className="border-b last:border-0 bg-muted/5 hover:bg-muted/10 transition-colors text-xs">
                          <td className="px-6 py-3 pl-12 text-muted-foreground flex items-center gap-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40"></div>
                            {modelo}
                          </td>
                          <td className="px-4 py-3 text-right">{metrics.publicaciones}</td>
                          <td className="px-4 py-3 text-right text-green-600/80">{metrics.adjudicadas}</td>
                          <td className="px-4 py-3 text-right text-red-600/80">{metrics.noAdjudicadas}</td>
                          <td className="px-4 py-3 text-right">{metrics.ofertas}</td>
                          <td className="px-4 py-3 text-right">${metrics.montoAdjudicado.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">{metrics.montoMinimo !== null ? `$${metrics.montoMinimo.toLocaleString()}` : '-'}</td>
                          <td className="px-4 py-3 text-right">{metrics.montoMaximo !== null ? `$${metrics.montoMaximo.toLocaleString()}` : '-'}</td>
                        </tr>
                      ))
                    }
                  </React.Fragment>
                ))}
              </tbody>
              <tfoot className="bg-muted/50 font-semibold border-t">
                <tr>
                  <td className="px-6 py-4">TOTALES GLOBALES</td>
                  <td className="px-4 py-4 text-right">{totalPubs}</td>
                  <td className="px-4 py-4 text-right text-green-600">{totalAdj}</td>
                  <td className="px-4 py-4 text-right text-red-600">{totalNoAdj}</td>
                  <td className="px-4 py-4 text-right">{totalOfertas}</td>
                  <td className="px-4 py-4 text-right">${totalMonto.toLocaleString()}</td>
                  <td className="px-4 py-4 text-right">{totalMinMonto !== null ? `$${totalMinMonto.toLocaleString()}` : '-'}</td>
                  <td className="px-4 py-4 text-right">{totalMaxMonto !== null ? `$${totalMaxMonto.toLocaleString()}` : '-'}</td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ReporteDinamico;
